<script>
/* eslint-disable vue/no-bare-strings-in-template */
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';

export default {
  name: 'AceleresWhatsappDisconnectedPanel',
  props: {
    inbox: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      reconnecting: false,
      reconnectModalOpen: false,
      reconnectError: '',
      reconnectQrBase64: '',
      reconnectPairingCode: '',
      reconnectStatusText: '',
    };
  },
  computed: {
    ...mapGetters({
      currentAccountId: 'getCurrentAccountId',
    }),
    inboxAttributes() {
      return this.inbox?.additional_attributes || {};
    },
    isDisconnected() {
      const status = this.inboxAttributes.connection_status;
      return typeof status === 'string' && status !== 'connected';
    },
    instanceId() {
      const id = Number(this.inboxAttributes.aceleres_instance_id || 0);
      return Number.isFinite(id) && id > 0 ? id : null;
    },
    canReconnect() {
      return this.isDisconnected && !!this.instanceId;
    },
    inboxName() {
      return (this.inbox?.name || '').replace(/ · [a-f0-9]{6}$/i, '');
    },
  },
  methods: {
    normalizeQrBase64(payload) {
      const raw =
        payload?.qr_base64 ||
        payload?.qrcode?.base64 ||
        payload?.qrcode?.qrcode?.base64 ||
        payload?.qrcode?.base64Qr ||
        payload?.qrcode?.code ||
        payload?.qrcode;

      if (!raw || typeof raw !== 'string') return '';
      return raw.startsWith('data:image')
        ? raw
        : `data:image/png;base64,${raw}`;
    },
    errorMessageFromResponse(error) {
      const data = error?.response?.data || {};
      return (
        data.message ||
        data.error ||
        data.detail ||
        error?.message ||
        'Não foi possível iniciar a reconexão.'
      );
    },
    closeReconnectModal() {
      this.reconnectModalOpen = false;
    },
    async reconnect() {
      if (this.reconnecting || !this.canReconnect) return;

      this.reconnecting = true;
      this.reconnectError = '';
      this.reconnectQrBase64 = '';
      this.reconnectPairingCode = '';
      this.reconnectStatusText =
        'Preparando a reconexão da instância existente...';
      this.reconnectModalOpen = true;

      try {
        const { data } = await window.axios.post(
          `/api/v1/accounts/${this.currentAccountId}/aceleres/whatsapp_instances/reconnect`,
          { inbox_id: this.inbox.id }
        );

        this.reconnectQrBase64 = this.normalizeQrBase64(data);
        this.reconnectPairingCode = data?.pairing_code || '';
        this.reconnectStatusText =
          data?.message || 'Escaneie o QR Code para reconectar esta instância.';

        if (!this.reconnectQrBase64 && !this.reconnectPairingCode) {
          this.reconnectError =
            'A reconexão foi iniciada, mas o QR Code ainda não ficou disponível. Tente novamente em alguns segundos.';
        }
      } catch (error) {
        this.reconnectError = this.errorMessageFromResponse(error);
        useAlert(this.reconnectError);
      } finally {
        this.reconnecting = false;
      }
    },
  },
};
</script>

<template>
  <section
    v-if="isDisconnected"
    class="aceleres-whatsapp-disconnected-panel"
    aria-live="polite"
  >
    <div class="aceleres-whatsapp-disconnected-panel__content">
      <div
        class="aceleres-whatsapp-disconnected-panel__icon"
        aria-hidden="true"
      >
        <span class="i-lucide-wifi-off" />
      </div>
      <div class="aceleres-whatsapp-disconnected-panel__message">
        <strong class="aceleres-whatsapp-disconnected-panel__title">
          Atendimento pausado
        </strong>
        <p class="aceleres-whatsapp-disconnected-panel__description">
          Esta instância do WhatsApp está desconectada. A mensagem não será
          enviada até a reconexão.
        </p>
      </div>
      <div class="aceleres-whatsapp-disconnected-panel__actions">
        <span
          v-if="inboxName"
          class="aceleres-whatsapp-disconnected-panel__instance"
        >
          <span
            class="aceleres-whatsapp-disconnected-panel__instance-icon i-lucide-server"
            aria-hidden="true"
          />
          <span class="truncate">Instância: {{ inboxName }}</span>
        </span>
        <button
          type="button"
          class="aceleres-whatsapp-disconnected-panel__reconnect"
          :disabled="reconnecting || !canReconnect"
          :title="
            canReconnect
              ? 'Reconectar esta instância via QR Code'
              : 'Instância sem vínculo para reconexão'
          "
          @click="reconnect"
        >
          <span class="i-lucide-qr-code" aria-hidden="true" />
          <span class="aceleres-whatsapp-disconnected-panel__reconnect-label">{{
            reconnecting ? 'Preparando QR Code...' : 'Reconectar via QR Code'
          }}</span>
        </button>
      </div>
    </div>
    <p v-if="!canReconnect" class="aceleres-whatsapp-disconnected-panel__error">
      O vínculo desta inbox não foi encontrado. Solicite a correção ao
      administrador.
    </p>
  </section>

  <woot-modal v-model:show="reconnectModalOpen" :on-close="closeReconnectModal">
    <woot-modal-header
      header-title="Reconectar WhatsApp"
      header-content="Use o WhatsApp do celular para escanear o QR Code desta instância existente."
    />
    <div class="px-6 pb-6 pt-2 text-n-slate-12">
      <div class="rounded-lg border border-n-weak bg-n-solid-1 p-4">
        <p class="m-0 text-sm text-n-slate-11">{{ reconnectStatusText }}</p>
        <div
          v-if="reconnectQrBase64"
          class="mt-4 flex flex-col items-center gap-3"
        >
          <img
            :src="reconnectQrBase64"
            alt="QR Code para reconectar WhatsApp"
            class="h-64 w-64 rounded-lg border border-n-weak bg-white p-3"
          />
          <p class="m-0 text-center text-sm text-n-slate-11">
            No celular, abra Dispositivos conectados e escaneie este QR Code.
          </p>
        </div>
        <div
          v-if="reconnectPairingCode"
          class="mt-4 rounded-lg bg-n-alpha-2 p-3 text-center"
        >
          <p class="m-0 text-xs font-semibold uppercase text-n-slate-10">
            Código de pareamento
          </p>
          <p
            class="m-0 mt-1 font-mono text-lg font-semibold tracking-wider text-n-slate-12"
          >
            {{ reconnectPairingCode }}
          </p>
        </div>
        <p
          v-if="reconnectError"
          class="m-0 mt-4 rounded-lg border border-red-100 bg-red-50 px-3 py-2 text-sm text-red-800"
        >
          {{ reconnectError }}
        </p>
        <div
          v-if="reconnecting && !reconnectQrBase64"
          class="mt-4 flex items-center justify-center gap-2 text-sm text-n-slate-11"
        >
          <span class="i-lucide-loader-circle size-4 animate-spin" />
          <span>Gerando QR Code...</span>
        </div>
      </div>
      <div class="mt-4 flex justify-end gap-2">
        <button
          type="button"
          class="inline-flex h-9 items-center rounded-lg border border-n-weak px-3 text-sm font-medium text-n-slate-12"
          @click="closeReconnectModal"
        >
          Fechar
        </button>
        <button
          type="button"
          class="inline-flex h-9 items-center gap-2 rounded-lg bg-n-brand px-3 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60"
          :disabled="reconnecting"
          @click="reconnect"
        >
          <span class="i-lucide-refresh-cw size-4" aria-hidden="true" />
          <span>Gerar novo QR</span>
        </button>
      </div>
    </div>
  </woot-modal>
</template>

<style scoped>
.aceleres-whatsapp-disconnected-panel {
  box-sizing: border-box;
  margin: 0.5rem;
  height: fit-content !important;
  min-height: 0;
  max-height: 8rem;
  flex: 0 0 auto;
  align-self: stretch;
  padding: 0.75rem 0.875rem;
  border: 1px solid #f1ddca;
  border-radius: 0.75rem;
  background: linear-gradient(135deg, #fffdfa 0%, #fff8ef 100%);
  overflow: hidden;
  box-shadow: 0 0.75rem 2rem rgba(104, 74, 45, 0.08);
  color: #454545;
}

.aceleres-whatsapp-disconnected-panel__content {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  min-width: 0;
}

.aceleres-whatsapp-disconnected-panel__icon {
  display: flex;
  width: 2.5rem;
  height: 2.5rem;
  flex: 0 0 2.5rem;
  align-items: center;
  justify-content: center;
  border-radius: 9999px;
  background: #ffeddc;
  color: #c45b16;
}

.aceleres-whatsapp-disconnected-panel__icon span {
  width: 1.25rem;
  height: 1.25rem;
}

.aceleres-whatsapp-disconnected-panel__message {
  min-width: 0;
  flex: 1 1 auto;
}

.aceleres-whatsapp-disconnected-panel__title {
  display: block;
  color: #68707a;
  font-size: 0.9375rem;
  font-weight: 600;
  line-height: 1rem;
}

.aceleres-whatsapp-disconnected-panel__description {
  max-width: 27rem;
  margin: 0.425rem 0 0;
  color: #68707a;
  font-size: 13px;
  line-height: 1.125rem;
}

.aceleres-whatsapp-disconnected-panel__actions {
  display: flex;
  width: auto;
  margin: 0 0 0 auto;
  flex: 0 0 auto;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: nowrap;
}

.aceleres-whatsapp-disconnected-panel__instance,
.aceleres-whatsapp-disconnected-panel__reconnect {
  display: inline-flex;
  height: 2.25rem;
  min-width: 0;
  align-items: center;
  justify-content: center;
  gap: 0.375rem;
  padding: 0 0.5rem;
  border: 1px solid #e0e1e3;
  border-radius: 10px;
  background: #fff;
  color: #68707a;
  font-size: 0.75rem;
  font-weight: 600;
  line-height: 1.2;
}

.aceleres-whatsapp-disconnected-panel__instance-icon,
.aceleres-whatsapp-disconnected-panel__reconnect > span:first-child {
  width: 1rem;
  height: 1rem;
  flex: 0 0 1rem;
}

.aceleres-whatsapp-disconnected-panel__reconnect {
  min-width: 0;
  border-color: #e0e1e3;
  color: #c45b16;
  cursor: pointer;
  transition:
    background-color 100ms ease,
    border-color 100ms ease;
}

.aceleres-whatsapp-disconnected-panel__reconnect:hover:not(:disabled) {
  border-color: #e6c4a5;
  background: #fff8f1;
}

.aceleres-whatsapp-disconnected-panel__reconnect:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.aceleres-whatsapp-disconnected-panel__error {
  margin: 0.5rem 0 0 3.125rem;
  color: #a44918;
  font-size: 0.75rem;
  line-height: 1rem;
}

@media (max-width: 1200px) {
  .aceleres-whatsapp-disconnected-panel {
    padding: 0.75rem 0.875rem;
  }
}

@media (max-width: 640px) {
  .aceleres-whatsapp-disconnected-panel {
    margin: 0.5rem;
    padding: 0.75rem 0.875rem;
  }

  .aceleres-whatsapp-disconnected-panel__content {
    flex-wrap: wrap;
  }

  .aceleres-whatsapp-disconnected-panel__icon {
    width: 2.5rem;
    height: 2.5rem;
    flex-basis: 2.5rem;
  }

  .aceleres-whatsapp-disconnected-panel__icon span {
    width: 1.25rem;
    height: 1.25rem;
  }

  .aceleres-whatsapp-disconnected-panel__message {
    min-width: 0;
  }

  .aceleres-whatsapp-disconnected-panel__title {
    font-size: 0.9375rem;
  }

  .aceleres-whatsapp-disconnected-panel__description {
    font-size: 0.875rem;
  }

  .aceleres-whatsapp-disconnected-panel__actions {
    flex-wrap: wrap;
    align-items: center;
    width: 100%;
    margin: 0.5rem 0 0 3.125rem;
  }

  .aceleres-whatsapp-disconnected-panel__instance,
  .aceleres-whatsapp-disconnected-panel__reconnect {
    min-width: 0;
    width: auto;
  }

  .aceleres-whatsapp-disconnected-panel__error {
    margin-left: 3.125rem;
  }
}
</style>
